import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useStore } from "store/zustand";
import { useUserStore } from "store/userStore";

interface PrivateRouteProps {
  children?: React.ReactNode;
  adminOnly?: boolean;
}

const ROUTES = {
  LOGIN: "/login",
  LOADS: "/search",
  ADMIN_DASHBOARD: "/admin/dashboard",
};

const VALID_ROLES = {
  ADMIN: "admin",
  USER: "user",
};

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  adminOnly = false,
}) => {
  const { isAuthenticated, setIsAuthenticated } = useStore();
  const { role, setUserRole } = useUserStore();
  const auth = getAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && isMounted) {
        try {
          await user.getIdToken();
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Error fetching auth token:", error);
          setIsAuthenticated(false);
        }
      } else if (isMounted) {
        setIsAuthenticated(false);
      }
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [setIsAuthenticated, auth]);

  useEffect(() => {
    if (isAuthenticated && !role) {
      const storedUser = localStorage.getItem("userDetails");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.state?.role) {
            setUserRole(parsedUser.state.role);
          }
        } catch (error) {
          console.error("Error parsing user details from localStorage:", error);
        }
      }
    }
    setRoleLoading(false);
  }, [isAuthenticated, role, setUserRole]);

  if (loading || roleLoading) {
    return;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (adminOnly && role === VALID_ROLES.USER) {
    return <Navigate to={ROUTES.LOADS} replace />;
  }

  if (role === VALID_ROLES.ADMIN && location.pathname === "/search") {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
